import React from 'react';
import {
  Card, CardContent, Typography, Avatar, Box, IconButton,
  Menu, MenuItem, useTheme, Switch, Chip, Divider, alpha, Stack,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { IUser } from '../definitions/app/user.d';

const roleColor = (role: string, theme: any) => {
  switch (role) {
    case 'superadmin': return theme.palette.error.main;
    case 'admin': return theme.palette.secondary.main;
    case 'user': return theme.palette.primary.main;
    default: return theme.palette.info.main;
  }
};

interface UserCardProps {
  user: IUser;
  onEditRole: (user: IUser) => void;
  onToggleActive: (user: IUser) => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, onEditRole, onToggleActive }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const color = roleColor(user.role, theme);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(color, 0.35),
          boxShadow: `0 4px 16px ${alpha(color, 0.12)}`,
        },
        borderTop: `3px solid ${color}`,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Avatar */}
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.12),
              color: color,
              fontWeight: 700,
              width: 44,
              height: 44,
              fontSize: '1.1rem',
              flexShrink: 0,
            }}
          >
            {user.name.charAt(0).toUpperCase()}
          </Avatar>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {user.name}
              </Typography>
              <IconButton size="small" onClick={handleClick} sx={{ flexShrink: 0, ml: 'auto' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {user.email}
            </Typography>

            {user.mobile && (
              <Typography variant="caption" color="text.secondary" display="block">
                {user.mobile}
              </Typography>
            )}

            <Stack direction="row" alignItems="center" spacing={1} mt={0.75}>
              <Chip
                icon={<AdminPanelSettingsIcon sx={{ fontSize: '12px !important' }} />}
                label={user.role}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: alpha(color, 0.1),
                  color: color,
                  '& .MuiChip-icon': { color: color },
                }}
              />
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {user.active ? 'Active' : 'Inactive'}
          </Typography>
          <Switch
            checked={user.active}
            onChange={() => onToggleActive(user)}
            size="small"
            color="primary"
          />
        </Stack>
      </CardContent>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => { onEditRole(user); handleClose(); }}
          sx={{ fontSize: '0.875rem', gap: 1 }}
        >
          Change Role
        </MenuItem>
        <MenuItem
          onClick={() => { onToggleActive(user); handleClose(); }}
          sx={{ fontSize: '0.875rem', gap: 1, color: user.active ? 'error.main' : 'success.main' }}
        >
          {user.active ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default UserCard;
